// Player.jsx with token support added to buildStreamUrl
import {
  createEffect,
  onCleanup,
  createMemo,
  on,
  createSignal,
  createSelector,
  onMount,
  Show,
} from "solid-js";
import { ElementNode, IntrinsicTextNodeStyleProps, View, hexColor, deg2Rad, Text } from "@lightningtv/solid";
import { useNavigate } from "@solidjs/router";
import { useParams, useLocation } from "@solidjs/router";
import gsap from "gsap";
import {
  isBuffering,
  playListPassword,
  playListUrl,
  playListUsername,
  setIsBuffering,
  setPreviewToNotShow,
} from "../../../state";
import videoPlayer from "../../video";
import { ProgressBar } from "@lightningtv/solid-ui";
import LoadingIndicator from "../loading/loading";
import { Buffer } from "buffer";
import theme from "theme";
import styles from "../../styles";
import { dnsResolver } from "../../api/providers/dnsResolver";
import { fetchMovieToken, buildMovieStreamUrl } from "../../utils/tokenHelper"; // ✅ Import token helpers
import axios from "axios"; // ✅ Import axios for API calls
import { clientBranding } from "../../config/clientConfig";

const TitleStyles = {
  fontFamily: "Roboto",
  fontWeight: 700,
  fontSize: 42,
  color: '#ffffff'
};

const Player = () => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = createSignal(null);
  const [isVideoPlaying, setIsVideoPlaying] = createSignal(false);
  const [videoDuration, setVideoDuration] = createSignal(0);
  const [currentTime, setCurrentTime] = createSignal(0);
  const [displayTime, setDisplayTime] = createSignal(0);
  const [contentType, setContentType] = createSignal("");
  const [nameMovie, setNameMovie] = createSignal("");
  const [progress, setProgress] = createSignal(0);
  const [extencion, setExtencion] = createSignal("");
  const [storedSeekTime, setStoredSeekTime] = createSignal(0);
  const [seekError, setSeekError] = createSignal("");
  const [isSeekingUI, setIsSeekingUI] = createSignal(false);
  const [showLoading, setShowLoading] = createSignal(true);
  const [isNavigating, setIsNavigating] = createSignal(false);
  const [showExitModal, setShowExitModal] = createSignal(false);
  const [exitModalFocused, setExitModalFocused] = createSignal("cancel"); // "cancel" or "exit"
  const [movieData, setMovieData] = createSignal<any>(null); // ✅ NEW: Store movie data for token

  let mainContentRef: any;
  let loadingScreenRef: any;
  let controlsContainerRef: any;
  let exitButtonRef: any;
  let cancelButtonRef: any;
  let playPauseRef: any;

  const location = useLocation();
  let updateInterval;
  const [controlsVisible, setControlsVisible] = createSignal(true);
  let controlsTimeout;

  const showControls = () => {
    setControlsVisible(true);

    // Animate controls in
    if (controlsContainerRef) {
      gsap.to(controlsContainerRef, {
        alpha: 1,
        duration: 0.3,
        ease: "power2.out"
      });
    }

    resetControlsTimeout();
  };

  const hideControls = () => {
    setControlsVisible(false);

    // Animate controls out
    if (controlsContainerRef) {
      gsap.to(controlsContainerRef, {
        alpha: 0,
        duration: 0.3,
        ease: "power2.out"
      });
    }
  };

  const resetControlsTimeout = () => {
    clearTimeout(controlsTimeout);
    controlsTimeout = setTimeout(hideControls, 6000);
  };

  // Helper function for smooth navigation with fade out
  const handleNavigate = (path: string) => {
    if (isNavigating()) return;
    setIsNavigating(true);

    gsap.to(mainContentRef, {
      alpha: 0,
      duration: 0.25,
      ease: "power2.inOut",
      onComplete: () => {
        navigate(path);
        setIsNavigating(false);
      }
    });
  };

  const [seriesId, setSeriesId] = createSignal<string | null>(null);

  createEffect(() => {
    const params = new URLSearchParams(location.search);
    const num = params.get("str");
    const type = params.get("type");
    const name = params.get("name") ? decodeURIComponent(params.get("name")) : "";
    const exc = params.get("extc");
    const storedSeek = params.get("seek");
    const series = params.get("series");

    console.log('Movie params:', { num, type, name, exc, storedSeek, series });

    if (num !== null) setCurrentIndex(num);
    if (type) setContentType(type);
    if (name) setNameMovie(name);
    if (exc) setExtencion(exc);
    if (storedSeek) setStoredSeekTime(Number(storedSeek) || 0);
    if (series) setSeriesId(series);
  });

  // ✅ NEW: Fetch movie data for token support
  const fetchMovieData = async (streamId: string, type: string) => {
    try {
      console.log("🔍 Fetching movie data for direct_source/token check...");
      const apiUrl = `${playListUrl()}/player_api.php?username=${playListUsername()}&password=${playListPassword()}&action=get_vod_info&vod_id=${streamId}`;

      const response = await axios.get(apiUrl);

      if (response.data && response.data.info) {
        const info = response.data.info;

        // Xtream Codes API stores direct_source/token/tokenUrl in movie_data, not info
        const movieData = response.data.movie_data;
        if (movieData?.direct_source) info.direct_source = movieData.direct_source;
        if (movieData?.token !== undefined) info.token = movieData.token;
        if (movieData?.tokenUrl !== undefined) info.tokenUrl = movieData.tokenUrl;

        console.log("✅ Movie data fetched:", {
          hasToken: info.token,
          hasTokenUrl: info.tokenUrl,
          directSource: info.direct_source ? info.direct_source.substring(0, 80) : null
        });
        setMovieData(info);
        return info;
      }
    } catch (error) {
      console.warn("⚠️ Could not fetch movie data:", error);
    }
    return null;
  };

  // ✅ NEW: Fetch series episode data for token support (mirrors fetchMovieData for movies)
  const fetchSeriesEpisodeData = async (episodeId: string, seriesIdValue: string) => {
    try {
      console.log("🔍 Fetching series episode data for direct_source/token check...");
      const apiUrl = `${playListUrl()}/player_api.php?username=${playListUsername()}&password=${playListPassword()}&action=get_series_info&series_id=${seriesIdValue}`;

      const response = await axios.get(apiUrl);
      const episodesBySeason = response.data?.episodes;
      if (!episodesBySeason) return null;

      let episode: any = null;
      for (const season of Object.values<any[]>(episodesBySeason)) {
        episode = season?.find((ep: any) => String(ep.id) === String(episodeId));
        if (episode) break;
      }

      if (episode) {
        console.log("✅ Episode data fetched:", {
          hasToken: episode.token,
          hasTokenUrl: episode.tokenUrl,
          directSource: episode.direct_source ? String(episode.direct_source).substring(0, 80) : null
        });
        setMovieData(episode);
        return episode;
      }
    } catch (error) {
      console.warn("⚠️ Could not fetch series episode data:", error);
    }
    return null;
  };

  // ✅ UPDATED: buildStreamUrl with token support
  const buildStreamUrl = async (streamOfID, type, exce) => {
    console.log("🔍 Building stream URL with token support...");
    console.log("Parameters:", { streamOfID, type, exce });

    // ✅ STEP 1: Fetch movie/episode data if we don't have it yet
    let currentMovieData = movieData();
    if (!currentMovieData && type === "movie") {
      currentMovieData = await fetchMovieData(streamOfID, type);
    } else if (!currentMovieData && type === "series") {
      const currentSeriesId = seriesId();
      if (currentSeriesId) {
        currentMovieData = await fetchSeriesEpisodeData(streamOfID, currentSeriesId);
      }
    }

    // ✅ STEP 2: Check if movie requires token
    let tokenData: string | null = null;
    if (currentMovieData) {
      try {
        tokenData = await fetchMovieToken(currentMovieData);
        if (tokenData) {
          console.log("🎟️ ✅ Movie token fetched");
        }
      } catch (error) {
        console.error("❌ Token fetch error:", error);
      }
    }

    // ✅ STEP 3: Build URL
    let videoUrl: string;

    if (tokenData && currentMovieData?.direct_source && currentMovieData.direct_source.trim() !== "") {
      // Movie tokens are a query string appended onto direct_source
      videoUrl = buildMovieStreamUrl(currentMovieData.direct_source.trim(), tokenData);
      console.log("🔗 Using direct_source + token as complete URL");
      return { originalUrl: videoUrl, finalUrl: videoUrl };
    } else if (currentMovieData?.direct_source && currentMovieData.direct_source.trim() !== "") {
      // Use direct_source URL set in backoffice — this is the actual stream URL
      // and bypasses the Xtream Codes proxy that may not handle HLS correctly
      videoUrl = currentMovieData.direct_source.trim();
      console.log("🔗 Using direct_source URL from backoffice:", videoUrl);
      return { originalUrl: videoUrl, finalUrl: videoUrl };
    } else {
      // Use the actual file extension from the channel data.
      // Hardcoding .m3u8 causes ExoPlayer to use HlsMediaSource, which then
      // fails when the server redirects to an .mp4 file (parser expects #EXTM3U).
      const ext = exce && exce !== "" ? exce : "m3u8";
      videoUrl = `${playListUrl()}/${type}/${playListUsername()}/${playListPassword()}/${streamOfID}.${ext}`;
      console.log("🔗 Using traditional URL:", videoUrl);
    }

    // ✅ STEP 4: Apply DNS resolver (only for traditional URLs)
    let finalVideoUrl = videoUrl;

    try {
      const dnsResult = await dnsResolver.transformUrl(videoUrl);
      if (typeof dnsResult === 'object' && dnsResult.url) {
        finalVideoUrl = dnsResult.url;
        console.log("🔗 DNS resolver applied");
      } else if (typeof dnsResult === 'string' && dnsResult !== videoUrl) {
        finalVideoUrl = dnsResult;
        console.log("🔗 DNS resolver applied (string)");
      }
    } catch (dnsError) {
      console.warn("⚠️ DNS transformation failed:", dnsError);
    }

    // ✅ STEP 5: Apply VPN proxy (only for traditional URLs)
    if (localStorage.getItem("vpn") === "on" && localStorage.getItem("proxy_url")) {
      const streamUrlProxy = Buffer.from(finalVideoUrl, "utf-8").toString("base64");
      finalVideoUrl = localStorage.getItem("proxy_url") + streamUrlProxy;
      console.log("🔗 VPN proxy applied");
    }

    console.log("🔗 Final URL:", finalVideoUrl.substring(0, 150) + "...");
    return { originalUrl: videoUrl, finalUrl: finalVideoUrl };
  };

  onMount(() => {
    console.log("🎬 Player mounted");

    // GSAP Timeline for page entrance
    const timeline = gsap.timeline();

    timeline.to(loadingScreenRef, {
      alpha: 0,
      duration: 0.3,
      ease: "power2.out",
      delay: 0.5,
      onComplete: () => {
        setShowLoading(false);
      }
    });

    timeline.to(mainContentRef, {
      alpha: 1,
      duration: 0.4,
      ease: "power2.out"
    }, "-=0.15");
  });

  onCleanup(() => {
    if (updateInterval) {
      clearInterval(updateInterval);
    }
    clearTimeout(controlsTimeout);
  });

  createEffect(async () => {
    const streamOfID = currentIndex();
    const type = contentType();
    const exce = extencion();

    if (!streamOfID || !type || !exce) return;

    try {
      const { originalUrl, finalUrl } = await buildStreamUrl(streamOfID, type, exce);
      console.log("🎬 Starting movie playback");
      console.log("📋 Original URL:", originalUrl);
      console.log("📋 Final URL:", finalUrl.substring(0, 100) + "...");

      setIsBuffering(true);
      setPreviewToNotShow(false);

      await videoPlayer.playVideo(finalUrl, "fullscreen");

      console.log("✅ Movie playback started successfully");
      setIsVideoPlaying(true);
      resetControlsTimeout();
      setIsBuffering(false);

      const getDuration = async () => {
        try {
          await new Promise(resolve => setTimeout(resolve, 1000));
          const duration = videoPlayer.getDuration();
          console.log("📏 Movie duration:", Math.floor(duration / 1000), "seconds");
          setVideoDuration(duration);
          return duration;
        } catch (error) {
          console.error("Error getting duration:", error);
          return 0;
        }
      };

      const duration = await getDuration();

      const seekTo = storedSeekTime();
      if (seekTo > 0 && duration > 0) {
        console.log("Performing initial seek to:", Math.floor(seekTo / 1000), "seconds");

        try {
          accumulatedSeekTime = 0;
          isAccumulatingSeeks = false;
          setIsSeekingUI(false);

          const actualSeekTime = await videoPlayer.seek(seekTo);
          setCurrentTime(actualSeekTime);
          setDisplayTime(actualSeekTime);
          setProgress(actualSeekTime / duration);
          baseSeekTime = actualSeekTime;
          setIsBuffering(false);

          console.log("Initial seek completed to:", Math.floor(actualSeekTime / 1000), "seconds");
        } catch (error) {
          console.warn("Initial seek failed:", error);
          setSeekError("Could not skip to saved position");
          setTimeout(() => setSeekError(""), 3000);
          setIsBuffering(false);
        }
      } else {
        setIsBuffering(false);
      }

      const timeUpdateHandler = () => {
        if (!location.pathname.startsWith("/player")) return;

        try {
          const playerState = videoPlayer.getState();

          if (playerState.ready && !playerState.seeking && playerState.state === "PLAYING") {
            const newTime = videoPlayer.getCurrentTime();

            if (!isSeekingUI() && !isAccumulatingSeeks) {
              const timeDiff = Math.abs(newTime - currentTime());

              if (timeDiff > 100) {
                setCurrentTime(newTime);
                setDisplayTime(newTime);

                if (!isAccumulatingSeeks) {
                  baseSeekTime = newTime;
                }
              }
            } else if (!isSeekingUI() && !isAccumulatingSeeks) {
              setCurrentTime(newTime);
            }
          }
        } catch (error) {
          if (!error.message.includes("not ready") && !error.message.includes("INVALID_STATE")) {
            console.warn("Time update error:", error.message);
          }
        }
      };

      updateInterval = setInterval(timeUpdateHandler, 500);

      setTimeout(() => {
        if (updateInterval) {
          clearInterval(updateInterval);
          updateInterval = setInterval(timeUpdateHandler, 1000);
        }
      }, 10000);

    } catch (error) {
      console.error("❌ Error in movie setup:", error);
      setIsVideoPlaying(false);
      setIsBuffering(false);
      setSeekError("Movie playback failed");
      setTimeout(() => setSeekError(""), 5000);
    }
  });

  const performExit = async () => {
    try {
      if (updateInterval) {
        clearInterval(updateInterval);
        updateInterval = null;
      }

      if (window.AndroidVideoPlayer && window.AndroidVideoPlayer.restoreAppFocus) {
        window.AndroidVideoPlayer.restoreAppFocus();
      }

      const actualTime = currentTime();
      const streamOfID = currentIndex();
      const type = contentType();
      const exce = extencion();

      if (streamOfID && type && exce && actualTime > 0) {
        const { originalUrl } = await buildStreamUrl(streamOfID, type, exce);
        const duration = videoDuration();

        if (actualTime < duration - 30000) {
          localStorage.setItem("vod_resume", JSON.stringify({
            videoUrl: originalUrl,
            currentTime: actualTime
          }));
          console.log("Resume position saved:", Math.floor(actualTime / 1000), "seconds");
        }
      }
    } catch (error) {
      console.warn("Error saving resume data:", error);
    }

    if (window.AndroidVideoPlayer) {
      try {
        window.AndroidVideoPlayer.stopVideo();
        window.AndroidVideoPlayer.enableLiveTVMode(false);
      } catch (e) {
        console.warn("Error stopping Android player:", e);
      }
    }

    videoPlayer.destroy();
    setPreviewToNotShow(true);

    // Animate out before navigating
    gsap.to(mainContentRef, {
      alpha: 0,
      duration: 0.25,
      ease: "power2.inOut",
      onComplete: () => {
        setTimeout(() => {
          const canvas = document.getElementsByTagName("canvas")[0];
          if (canvas) {
            canvas.focus();
          }

          document.dispatchEvent(new CustomEvent('restoreKeyHandling'));

          navigate(-1);
        }, 100);
      }
    });
  };

  const onCustomEscape = () => {
    // Show exit confirmation modal
    setShowExitModal(true);
    setExitModalFocused("cancel");

    // Pause video while modal is shown
    if (isVideoPlaying()) {
      videoPlayer.pause();
    }
  };

  const handleExitConfirm = async () => {
    setShowExitModal(false);
    await performExit();
  };

  const handleExitCancel = () => {
    setShowExitModal(false);
    setExitModalFocused("cancel");

    // Resume video if it was playing
    if (isVideoPlaying()) {
      videoPlayer.resume();
    }
  };

  const togglePlayPause = () => {
    try {
      if (isVideoPlaying()) {
        videoPlayer.pause();
        setIsVideoPlaying(false);
      } else {
        videoPlayer.resume();
        setIsVideoPlaying(true);
      }
      showControls();
    } catch (error) {
      console.error("Error toggling play/pause:", error);
      setSeekError("Playback control error");
      setTimeout(() => setSeekError(""), 2000);
    }
  };

  createEffect(() => {
    const current = currentTime();
    const duration = videoDuration();

    if (!isFinite(current) || !isFinite(duration) || duration <= 0) return;

    const progressValue = Math.max(0, Math.min(current / duration, 1));
    setProgress(progressValue);
  });

  let seekDebounceTimeout;
  let accumulatedSeekTime = 0;
  let baseSeekTime = 0;
  let isAccumulatingSeeks = false;

  const seekTime = (seconds) => {
    try {
      if (!videoPlayer.isReady()) {
        setSeekError("Player not ready");
        setTimeout(() => setSeekError(""), 2000);
        return;
      }

      const current = currentTime();
      const duration = videoDuration();

      if (!isFinite(current) || !isFinite(duration) || duration <= 0) {
        console.warn("Invalid time values for seeking");
        return;
      }

      if (!isAccumulatingSeeks) {
        baseSeekTime = current;
        accumulatedSeekTime = 0;
        isAccumulatingSeeks = true;
        setIsSeekingUI(true);
      }

      accumulatedSeekTime += seconds * 1000;

      const targetTime = baseSeekTime + accumulatedSeekTime;
      const clampedTime = Math.max(0, Math.min(targetTime, duration - 2000));

      setDisplayTime(clampedTime);
      setProgress(clampedTime / duration);

      console.log(`🎯 Seeking: base=${Math.floor(baseSeekTime / 1000)}s + accumulated=${Math.floor(accumulatedSeekTime / 1000)}s = target=${Math.floor(clampedTime / 1000)}s`);

      clearTimeout(seekDebounceTimeout);

      seekDebounceTimeout = setTimeout(async () => {
        try {
          console.log("🚀 Executing accumulated seek to:", Math.floor(clampedTime / 1000), "seconds");

          const actualSeekTime = await videoPlayer.seek(clampedTime);

          setCurrentTime(actualSeekTime);
          setDisplayTime(actualSeekTime);
          setProgress(actualSeekTime / duration);

          accumulatedSeekTime = 0;
          baseSeekTime = actualSeekTime;
          isAccumulatingSeeks = false;
          setIsSeekingUI(false);

          console.log("✅ Accumulated seek completed at:", Math.floor(actualSeekTime / 1000), "seconds");
          setSeekError("");

        } catch (error) {
          console.error("❌ Accumulated seek failed:", error);

          accumulatedSeekTime = 0;
          isAccumulatingSeeks = false;
          setIsSeekingUI(false);

          setTimeout(() => {
            try {
              const recoveredTime = videoPlayer.getCurrentTime();
              setCurrentTime(recoveredTime);
              setDisplayTime(recoveredTime);
              setProgress(recoveredTime / duration);
              baseSeekTime = recoveredTime;
              console.log("🔄 Recovered to time:", Math.floor(recoveredTime / 1000), "seconds");
            } catch (recoveryError) {
              console.warn("Could not recover time position");
            }
          }, 500);

          setSeekError("Seek failed - recovered");
          setTimeout(() => setSeekError(""), 2000);
        }
      }, 700);

      showControls();

    } catch (error) {
      console.error("Error in seekTime:", error);
      accumulatedSeekTime = 0;
      isAccumulatingSeeks = false;
      setIsSeekingUI(false);
      setSeekError("Seek error");
      setTimeout(() => setSeekError(""), 2000);
    }
  };

  const onKeyPressActions = () => {
    showControls();
    resetControlsTimeout();
  };

  function formatTime(ms) {
    if (!isFinite(ms) || ms < 0) return "00:00:00";

    let totalSeconds = Math.floor(ms / 1000);
    let hours = Math.floor(totalSeconds / 3600);
    let minutes = Math.floor((totalSeconds % 3600) / 60);
    let seconds = totalSeconds % 60;

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return (
    <View>
      {/* Loading Screen */}
      <Show when={showLoading()}>
        <View
          ref={loadingScreenRef}
          width={1920}
          height={1080}
          color={hexColor("#000000")}
          alpha={1}
          style={{ zIndex: 2000, position: "absolute" }}
        >
          <View x={900} y={400}>
            <LoadingIndicator />
          </View>
        </View>
      </Show>

      {/* Main Content */}
      <View
        ref={mainContentRef}
        alpha={0}
        onEscape={() => {
          if (!showExitModal()) {
            onCustomEscape();
            console.log("Escape key detected in Player - showing exit modal");
          }
        }}
        onLeft={() => {
          if (!showExitModal()) {
            seekTime(-15);
            onKeyPressActions();
          }
        }}
        onRight={() => {
          if (!showExitModal()) {
            seekTime(15);
            onKeyPressActions();
          }
        }}
        onEnter={() => {
          if (!showExitModal()) {
            togglePlayPause();
            onKeyPressActions();
          }
        }}
        onUp={() => {
          if (!showExitModal()) {
            onKeyPressActions();
          }
        }}
        onDown={() => {
          if (!showExitModal()) {
            onKeyPressActions();
            playPauseRef?.setFocus?.();
          }
        }}
        autofocus={!showExitModal()}
      >
        <Show when={isBuffering()}>
          <View x={850} y={400} style={{ zIndex: 100 }}>
            <LoadingIndicator />
          </View>
        </Show>

        {/* Show seek error if any */}
        <Show when={seekError()}>
          <View x={760} y={300} style={{
            zIndex: 1500
          }}>
            <View
              color={hexColor("#FF0000")}
              alpha={0.9}
              borderRadius={8}
              width={400}
              height={80}
            >
              <Text fontSize={24} color={hexColor("#FFFFFF")} x={200} y={30} style={{ textAlign: "center" }}>
                {seekError()}
              </Text>
            </View>
          </View>
        </Show>

        {/* Controls Container with animation */}
        <View ref={controlsContainerRef} alpha={1}>
          {/* Top and bottom scrims — real linear gradients via the
              renderer's own colorTop/colorBottom interpolation, not fixed-
              size external images (the two previous ones had no width/
              height set at all, so they rendered at whatever size the
              source image happened to be, not the actual screen). Native
              gradients always fill exactly 1920x1080 regardless of
              resolution. */}
          <View
            width={1920}
            height={260}
            style={{
              colorTop: hexColor("#000000B3"),
              colorBottom: hexColor("#00000000"),
            }}
          />
          <View
            y={680}
            width={1920}
            height={400}
            style={{
              colorTop: hexColor("#00000000"),
              colorBottom: hexColor("#000000D9"),
            }}
          />

          {/* Movie Title - Top Left */}
          <Text style={TitleStyles} y={50} x={80}>
            {nameMovie()}
          </Text>

          {/* Time Display - Above Progress Bar */}
          <Text y={1020} fontSize={24} x={690} color={hexColor("#B9B9BE")}>
            {formatTime(displayTime())}
          </Text>

          {/* Progress Bar — accent color shifts while scrubbing, so the
              seek itself reads as a distinct state, not just a moving
              fill */}
          <View
            y={967}
            x={400}
            style={{
              width: 1200,
              height: 15
            }}
          >
            <ProgressBar
              progress={progress()}
              borderRadius={5}
              height={15}
              width={1200}
              zIndex={99}
              value={0}
              y={0}
              progressRadius={100}
              containerColor={hexColor("#404040")}
              progressColor={isSeekingUI() ? hexColor(clientBranding().primaryColor) : hexColor("#ffffff")}
            />
          </View>

          {/* Total Duration */}
          <Text fontSize={24} y={1020} x={1220} color={hexColor("#B9B9BE")}>{formatTime(videoDuration())}</Text>

          {/* Play/Pause Button — now a real focus target: Down from the
              main view lands here, Enter toggles playback, Left/Right
              still seek (same as from the main view), Up returns focus */}
          <Show when={!isBuffering()}>
            <View
              ref={playPauseRef}
              style={{
                width: 64,
                height: 64,
                borderRadius: 32,
                scale: 1,
                color: hexColor("#00000080"),
                border: { width: 2, color: hexColor("#FFFFFF40") },
                transition: {
                  scale: { duration: 150, easing: "ease-in-out" },
                  border: { duration: 150 },
                },
                focus: {
                  scale: 1.1,
                  color: hexColor("#000000B3"),
                  border: { width: 3, color: hexColor("#ffffff") },
                },
              }}
              x={970}
              y={1000}
              onEnter={() => {
                togglePlayPause();
                onKeyPressActions();
              }}
              onUp={() => {
                onKeyPressActions();
                mainContentRef?.setFocus?.();
              }}
              onLeft={() => {
                seekTime(-15);
                onKeyPressActions();
              }}
              onRight={() => {
                seekTime(15);
                onKeyPressActions();
              }}
            >
              <View
                src={
                  isVideoPlaying()
                    ? "https://i.ibb.co/LDXv0w9X/pause.png"
                    : "https://i.ibb.co/SDHcywjP/play-button.png"
                }
                x={isVideoPlaying() ? 7 : 19}
                y={isVideoPlaying() ? 7 : 17}
                width={isVideoPlaying() ? 50 : 30}
                height={isVideoPlaying() ? 50 : 30}
              />
            </View>
          </Show>
        </View>

        {/* Exit Confirmation Modal */}
        <Show when={showExitModal()}>
          <View
            width={1920}
            height={1080}
            color={hexColor("#000000")}
            alpha={0.85}
            style={{ zIndex: 2500 }}
          >
            {/* Modal Container */}
            <View
              x={560}
              y={340}
              width={800}
              height={400}
              color={hexColor("#1a1a1a")}
              borderRadius={16}
              style={{ zIndex: 2600 }}
            >
              {/* Title */}
              <Text
                x={300}
                y={80}
                fontSize={38}
                fontWeight={700}
                color={hexColor("#ffffff")}
                style={{ textAlign: "center" }}
              >
                Exit Video?
              </Text>

              {/* Message */}
              <Text
                x={250}
                y={150}
                fontSize={24}
                color={hexColor("#cccccc")}
                style={{ textAlign: "center" }}
              >
                Are you sure you want to exit?
              </Text>

              {/* Buttons Container */}
              <View x={50} y={250}>
                {/* Cancel Button */}
                <View
                  ref={cancelButtonRef}
                  x={80}
                  y={0}
                  width={250}
                  height={80}
                  color={exitModalFocused() === "cancel" ? hexColor("#ffffff") : hexColor("#3a3a3a")}
                  borderRadius={12}
                  autofocus={exitModalFocused() === "cancel"}
                  onEnter={handleExitCancel}
                  onRight={() => {
                    setExitModalFocused("exit");
                    if (exitButtonRef) exitButtonRef.setFocus();
                  }}
                  onEscape={handleExitCancel}
                  onFocus={() => setExitModalFocused("cancel")}
                  style={{
                    transition: "all 0.2s ease"
                  }}
                >
                  <Text
                    x={75}
                    y={28}
                    fontSize={28}
                    fontWeight={600}
                    color={exitModalFocused() === "cancel" ? hexColor("#000000") : hexColor("#ffffff")}
                    style={{ textAlign: "center" }}
                  >
                    Cancel
                  </Text>
                </View>

                {/* Exit Button */}
                <View
                  ref={exitButtonRef}
                  x={370}
                  y={0}
                  width={250}
                  height={80}
                  color={exitModalFocused() === "exit" ? hexColor("#e50914") : hexColor("#3a3a3a")}
                  borderRadius={12}
                  autofocus={exitModalFocused() === "exit"}
                  onEnter={handleExitConfirm}
                  onLeft={() => {
                    setExitModalFocused("cancel");
                    if (cancelButtonRef) cancelButtonRef.setFocus();
                  }}
                  onEscape={handleExitCancel}
                  onFocus={() => setExitModalFocused("exit")}
                  style={{
                    transition: "all 0.2s ease"
                  }}
                >
                  <Text
                    x={100}
                    y={28}
                    fontSize={28}
                    fontWeight={600}
                    color={hexColor("#ffffff")}
                    style={{ textAlign: "center" }}
                  >
                    Exit
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </Show>
      </View>
    </View>
  );
};

export default Player;